import React, { ChangeEvent, FC, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Box,
  Button,
  Checkbox,
  Dialog,
  DialogActions,
  DialogContent,
  FormControl,
  FormControlLabel,
  FormGroup,
  Unstable_Grid2 as Grid,
  Typography,
} from '@mui/material';
import { Form, Formik, FormikProps } from 'formik';

import { GenericStatus, useCreateGenericWebhookMutation, WebhookEvents } from 'api';
import { useNotify } from 'hooks';
import { Loaders, ValidatedTextFieldNext } from 'components';
import { GenericWebhookSchema } from './Schema';
import { useCreateGenericWebhook } from 'features/admin/updaters/useCreateWebhook';

interface Props {
  isOpen: boolean;
  setIsOpen: (isOpen: boolean) => void;
}

const GenericWebhookModal: FC<Props> = ({ isOpen, setIsOpen }) => {
  const { t } = useTranslation();
  const notify = useNotify();
  const formRef = useRef<FormikProps<any>>(null);

  const initialValues = {
    callback_url: '',
    secret_token: '',
    webhook_events: [] as WebhookEvents[],
    webhook_name: '',
    webhook_status: GenericStatus.Active,
  };

  const queryOptions = useCreateGenericWebhook();
  const { mutate, isLoading } = useCreateGenericWebhookMutation({
    onError: () => setIsOpen(false),
    onSuccess,
    ...queryOptions,
  });

  function getEventName(event: WebhookEvents) {
    switch (event) {
      case WebhookEvents.ContentCreated:
        return <Typography variant="subtitle2">{t('admin:contentCreated')}</Typography>;
      case WebhookEvents.ContentDeleted:
        return <Typography variant="subtitle2">{t('admin:contentDeleted')}</Typography>;
      case WebhookEvents.ContentPublished:
        return <Typography variant="subtitle2">{t('admin:contentPublished')}</Typography>;
      case WebhookEvents.ContentUpdated:
        return <Typography variant="subtitle2">{t('admin:contentUpdated')}</Typography>;
    }
  }

  function getEventBody(event: WebhookEvents) {
    switch (event) {
      case WebhookEvents.ContentCreated:
        return 'When a particular content_id is created.';
      case WebhookEvents.ContentDeleted:
        return 'When a content is deleted from a group.';
      case WebhookEvents.ContentPublished:
        return 'When a content is published to a group.';
      case WebhookEvents.ContentUpdated:
        return 'When a particular content_id is updated.';
    }
  }

  function onSuccess() {
    setIsOpen(false);
    formRef.current?.resetForm();
    notify.info(t('admin:webhookCreated'));
  }

  function onClickCancel() {
    setIsOpen(false);
    formRef.current?.resetForm();
  }

  function onSubmit(values: typeof initialValues) {
    mutate({ ...values });
  }

  return (
    <Formik
      initialValues={initialValues}
      onSubmit={onSubmit}
      innerRef={formRef}
      validationSchema={GenericWebhookSchema}
      validateOnBlur={false}
    >
      {({ isValid, setFieldError, values, setFieldValue }) => (
        <Form>
          <Dialog open={isOpen} fullWidth maxWidth="xs">
            <DialogContent>
              <Box marginBottom={5}>
                <Grid container alignItems="center" justifyContent="center">
                  <Grid>
                    <Typography variant="h6">{t('admin:createNewWebhook')}</Typography>
                  </Grid>
                  <Grid>
                    <Typography
                      variant="body1"
                      color="textSecondary"
                      dangerouslySetInnerHTML={{ __html: t('admin:learnMoreIntegrations') }}
                    />
                  </Grid>
                </Grid>
              </Box>

              <Grid container direction="column" spacing={3}>
                <Grid xs={12}>
                  <ValidatedTextFieldNext
                    fullWidth
                    label={t('common:name')}
                    InputLabelProps={{ shrink: true }}
                    name="webhook_name"
                    onFocus={() => setFieldError('webhook_name', '')}
                  />
                </Grid>

                <Grid xs={12}>
                  <ValidatedTextFieldNext
                    fullWidth
                    label={t('admin:payloadUrl')}
                    InputLabelProps={{ shrink: true }}
                    name="callback_url"
                    onFocus={() => setFieldError('callback_url', '')}
                  />
                </Grid>

                <Grid xs={12}>
                  <ValidatedTextFieldNext
                    fullWidth
                    label={t('common:secret')}
                    InputLabelProps={{ shrink: true }}
                    name="secret_token"
                    onFocus={() => setFieldError('secret_token', '')}
                  />
                </Grid>

                <Grid xs={12}>
                  <FormControl variant="standard" fullWidth required component="fieldset">
                    <FormGroup>
                      {Object.values(WebhookEvents).map((event, idx) => (
                        <Grid container key={idx}>
                          <Grid>
                            <FormControlLabel
                              key={event}
                              control={
                                <Checkbox
                                  checked={values.webhook_events.includes(event)}
                                  onChange={(e: ChangeEvent<HTMLInputElement>) => {
                                    const checked = e.target.checked;
                                    setFieldValue(
                                      'webhook_events',
                                      checked
                                        ? [...values.webhook_events, event]
                                        : values.webhook_events.filter(
                                            (ev: WebhookEvents) => ev !== event
                                          )
                                    );
                                  }}
                                  name={event}
                                />
                              }
                              label={getEventName(event)}
                            />
                          </Grid>

                          <Grid container>
                            <Grid xs={1} />
                            <Grid>
                              <Box marginTop={-1.5} marginBottom={2}>
                                <Typography variant="body1" color="textSecondary">
                                  {getEventBody(event)}
                                </Typography>
                              </Box>
                            </Grid>
                          </Grid>
                        </Grid>
                      ))}
                    </FormGroup>
                  </FormControl>
                </Grid>
              </Grid>
            </DialogContent>
            <DialogActions style={{ marginTop: '8px', marginBottom: '32px' }}>
              <Grid container alignItems="center" justifyContent="center" spacing={2}>
                <Grid>
                  <Button fullWidth variant="contained" size="large" onClick={onClickCancel}>
                    {t('common:cancel')}
                  </Button>
                </Grid>

                <Grid>
                  <Button
                    type="submit"
                    fullWidth
                    variant="contained"
                    color="primary"
                    size="large"
                    disabled={isLoading || !isValid}
                  >
                    {isLoading ? <Loaders.Standard height={26} /> : t('common:connect')}
                  </Button>
                </Grid>
              </Grid>
            </DialogActions>
          </Dialog>
        </Form>
      )}
    </Formik>
  );
};

export default GenericWebhookModal;
